import React, { useState, useEffect } from 'react';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { Checkbox } from '../../../components/ui/Checkbox';
import StripePaymentForm from '../../../components/payment/StripePaymentForm';
import { paymentService } from '../../../services/paymentService';
import { serviceZoneService } from '../../../services/serviceZoneService';
import { supabase } from '../../../lib/supabase';
import { smsNotificationService } from '../../../services/smsNotificationService';

const PaymentStep = ({ data, onUpdate, onComplete, onBack }) => {
  const [customerInfo, setCustomerInfo] = useState({
    firstName: data?.firstName || '',
    lastName: data?.lastName || '',
    email: data?.email || '',
    phone: data?.phone || '',
    address: data?.address || '',
    postalCode: data?.postalCode || '',
    city: data?.city || '',
    customerType: data?.customerType || 'individual',
  });

  const [paymentMethod, setPaymentMethod] = useState(data?.paymentMethod || 'card');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptRGPD, setAcceptRGPD] = useState(false);
  const [createAccount, setCreateAccount] = useState(false);
  const [errors, setErrors] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);
  const [showValidationSummary, setShowValidationSummary] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);
  const [paymentIntentId, setPaymentIntentId] = useState(null);
  const [appointmentId, setAppointmentId] = useState(null);
  const [showStripeForm, setShowStripeForm] = useState(false);
  const [isValidatingLocation, setIsValidatingLocation] = useState(false);
  const [locationValidated, setLocationValidated] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  const isQuoteService = data?.service?.type === 'quote';
  const basePrice = data?.service?.price || 0;
  const subtotal = basePrice;
  const vatRate = customerInfo?.customerType === 'individual' ? 0.1 : 0.2;
  const vat = subtotal * vatRate;
  const total = subtotal + vat;

  // Check if user is logged in and load their address
  useEffect(() => {
    const checkUserSession = async () => {
      const { data: { session } } = await supabase?.auth?.getSession();
      if (session?.user) {
        setIsLoggedIn(true);
        // Load user's saved address if available
        const { data: profile } = await supabase
          ?.from('user_profiles')
          ?.select('address, postal_code, city')
          ?.eq('id', session?.user?.id)
          ?.single();
        
        if (profile && profile?.address) {
          setCustomerInfo(prev => ({
            ...prev,
            address: profile?.address || prev?.address,
            postalCode: profile?.postal_code || prev?.postalCode,
            city: profile?.city || prev?.city
          }));
        }
      }
    };
    checkUserSession();
  }, []);

  const handleCustomerInfoChange = (field, value) => {
    setCustomerInfo((prev) => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
    // Reset location validation when address fields change
    if (['address', 'postalCode', 'city']?.includes(field)) {
      setLocationValidated(false);
    }
  };

  const validateLocation = async () => {
    if (!customerInfo?.postalCode || !customerInfo?.city) {
      setErrors(prev => ({ ...prev, location: 'Veuillez renseigner le code postal et la ville' }));
      return false;
    }

    setIsValidatingLocation(true);
    setErrors(prev => ({ ...prev, location: '' }));

    try {
      const isValid = await serviceZoneService?.validateLocation(
        customerInfo?.postalCode,
        customerInfo?.city,
        null,
        null
      );

      if (isValid) {
        setLocationValidated(true);
        return true;
      } else {
        setErrors(prev => ({
          ...prev,
          location: `Désolé, nous n'intervenons pas encore dans votre zone (${customerInfo?.postalCode} ${customerInfo?.city}). Contactez-nous pour plus d'informations.`
        }));
        setLocationValidated(false);
        return false;
      }
    } catch (error) {
      console.error('Erreur validation zone:', error);
      setErrors(prev => ({
        ...prev,
        location: 'Erreur lors de la vérification de la zone. Veuillez réessayer.'
      }));
      setLocationValidated(false);
      return false;
    } finally {
      setIsValidatingLocation(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!customerInfo?.firstName?.trim()) {
      newErrors.firstName = 'Le prénom est requis';
    } else if (customerInfo?.firstName?.trim()?.length < 2) {
      newErrors.firstName = 'Le prénom doit contenir au moins 2 caractères';
    } else if (!/^[a-zA-ZÀ-ÿ\s-]+$/?.test(customerInfo?.firstName)) {
      newErrors.firstName = 'Le prénom contient des caractères invalides';
    }
    
    if (!customerInfo?.lastName?.trim()) {
      newErrors.lastName = 'Le nom est requis';
    } else if (customerInfo?.lastName?.trim()?.length < 2) {
      newErrors.lastName = 'Le nom doit contenir au moins 2 caractères';
    } else if (!/^[a-zA-ZÀ-ÿ\s-]+$/?.test(customerInfo?.lastName)) {
      newErrors.lastName = 'Le nom contient des caractères invalides';
    }
    
    if (!customerInfo?.email?.trim()) {
      newErrors.email = 'L\'email est requis';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(customerInfo?.email)) {
      newErrors.email = 'Format d\'email invalide (ex: nom@domaine.fr)';
    }
    
    if (!customerInfo?.phone?.trim()) {
      newErrors.phone = 'Le téléphone est requis';
    } else {
      const phoneDigits = customerInfo?.phone?.replace(/\s/g, '');
      if (!/^0[1-9][0-9]{8}$/?.test(phoneDigits)) {
        newErrors.phone = 'Numéro invalide (format: 06 12 34 56 78)';
      }
    }

    if (!customerInfo?.address?.trim()) {
      newErrors.address = 'L\'adresse est requise';
    }

    if (!customerInfo?.postalCode?.trim()) {
      newErrors.postalCode = 'Le code postal est requis';
    } else if (!/^[0-9]{5}$/?.test(customerInfo?.postalCode)) {
      newErrors.postalCode = 'Code postal invalide (5 chiffres)';
    }

    if (!customerInfo?.city?.trim()) {
      newErrors.city = 'La ville est requise';
    }

    if (!locationValidated) {
      newErrors.location = 'Veuillez valider votre zone d\'intervention';
    }

    if (!acceptTerms) {
      newErrors.terms = 'Vous devez accepter les conditions générales';
    }
    if (!acceptRGPD) {
      newErrors.rgpd = 'Vous devez accepter la politique de confidentialité';
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    const newErrors = validateForm();
    if (Object.keys(newErrors)?.length > 0) {
      setErrors(newErrors);
      setShowValidationSummary(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setShowValidationSummary(false);

    if (isQuoteService) {
      setIsProcessing(true);
      setTimeout(() => {
        onUpdate({
          ...customerInfo,
          paymentMethod,
          createAccount,
          total: 0,
        });
        onComplete();
      }, 1500);
      return;
    }

    if (paymentMethod === 'card') {
      setIsProcessing(true);
      try {
        const reference = `APT-${Date.now()}-${Math.random()?.toString(36)?.substr(2, 9)?.toUpperCase()}`;
        
        // Calculate scheduled date and time based on urgency
        const now = new Date();
        let scheduledDate = new Date();
        let scheduledTimeStart = '09:00:00'; // Default start time
        
        // Adjust scheduling based on urgency
        if (data?.urgency?.label === 'Urgence') {
          // Emergency: schedule for today or next available slot
          scheduledDate = now;
          const currentHour = now?.getHours();
          if (currentHour < 18) {
            scheduledTimeStart = `${String(currentHour + 1)?.padStart(2, '0')}:00:00`;
          } else {
            // After hours, schedule for next day morning
            scheduledDate?.setDate(scheduledDate?.getDate() + 1);
            scheduledTimeStart = '08:00:00';
          }
        } else if (data?.urgency?.label === "Aujourd'hui") {
          // Today: schedule for later today
          scheduledDate = now;
          const currentHour = now?.getHours();
          if (currentHour < 17) {
            scheduledTimeStart = `${String(currentHour + 2)?.padStart(2, '0')}:00:00`;
          } else {
            scheduledDate?.setDate(scheduledDate?.getDate() + 1);
            scheduledTimeStart = '09:00:00';
          }
        } else {
          // Standard: schedule for next business day
          scheduledDate?.setDate(scheduledDate?.getDate() + 1);
          // Skip weekend if necessary
          if (scheduledDate?.getDay() === 0) scheduledDate?.setDate(scheduledDate?.getDate() + 1); // Sunday -> Monday
          if (scheduledDate?.getDay() === 6) scheduledDate?.setDate(scheduledDate?.getDate() + 2); // Saturday -> Monday
          scheduledTimeStart = '09:00:00';
        }
        
        const scheduledDateString = scheduledDate?.toISOString()?.split('T')?.[0]; // Format: YYYY-MM-DD
        
        const { data: appointment, error: appointmentError } = await supabase?.from('appointments')?.insert({
            reference,
            customer_name: `${customerInfo?.firstName} ${customerInfo?.lastName}`,
            customer_email: customerInfo?.email,
            customer_phone: customerInfo?.phone,
            service_type: data?.service?.name || 'Service de plomberie',
            urgency: data?.urgency?.label || 'Standard',
            status: 'planned',
            scheduled_date: scheduledDateString,
            scheduled_time_start: scheduledTimeStart,
            customer_address: `${customerInfo?.address}, ${customerInfo?.postalCode} ${customerInfo?.city}`,
            customer_latitude: 48.8566,
            customer_longitude: 2.3522,
            notes: `${data?.room?.name} - ${data?.equipment?.name}`,
            price_subtotal: subtotal,
            price_vat: vat,
            price_total: total,
            payment_status: 'pending',
            payment_method: 'card'
          })?.select()?.single();

        if (appointmentError) throw appointmentError;

        setAppointmentId(appointment?.id);

        const orderData = {
          total: total,
          subtotal: subtotal,
          tax: vat,
          currency: 'EUR',
          reference: reference,
          items: [{
            name: data?.service?.name || 'Service de plomberie',
            price: total,
            quantity: 1
          }]
        };

        const customerData = {
          firstName: customerInfo?.firstName,
          lastName: customerInfo?.lastName,
          email: customerInfo?.email,
          phone: customerInfo?.phone,
          appointmentId: appointment?.id
        };

        const paymentData = await paymentService?.createPaymentIntent(orderData, customerData);
        
        setClientSecret(paymentData?.clientSecret);
        setPaymentIntentId(paymentData?.paymentIntentId);
        setShowStripeForm(true);
      } catch (error) {
        console.error('Erreur lors de la création du paiement:', error);
        setErrors({ submit: 'Erreur lors de la préparation du paiement. Veuillez réessayer.' });
      } finally {
        setIsProcessing(false);
      }
    }
  };

  const handlePaymentSuccess = async (result) => {
    try {
      if (appointmentId && result?.paymentIntent?.id) {
        await supabase?.from('appointments')?.update({
            payment_intent_id: result?.paymentIntent?.id,
            payment_status: 'succeeded'
          })?.eq('id', appointmentId);
      }

      try {
        const appointmentData = {
          reference: `APT-${Date.now()}`,
          customerName: `${customerInfo?.firstName} ${customerInfo?.lastName}`,
          customerEmail: customerInfo?.email,
          customerPhone: customerInfo?.phone,
          serviceType: data?.service?.name || 'Service de plomberie',
          customerAddress: `${customerInfo?.address}, ${customerInfo?.postalCode} ${customerInfo?.city}`
        };

        await smsNotificationService?.sendWithPreferenceCheck(
          appointmentData,
          'booking_confirmed'
        );
      } catch (smsError) {
        console.error('SMS notification failed:', smsError);
      }

      onUpdate({
        ...customerInfo,
        paymentMethod,
        createAccount,
        total,
        paymentIntentId: result?.paymentIntent?.id,
        paymentStatus: 'succeeded'
      });
      onComplete();
    } catch (error) {
      console.error('Erreur lors de la mise à jour du paiement:', error);
      onUpdate({
        ...customerInfo,
        paymentMethod,
        createAccount,
        total,
        paymentIntentId: result?.paymentIntent?.id,
        paymentStatus: 'succeeded'
      });
      onComplete();
    }
  };

  const handlePaymentError = (error) => {
    console.error('Erreur de paiement:', error);
    setErrors({ submit: error?.message || 'Le paiement a échoué. Veuillez réessayer.' });
    setShowStripeForm(false);
    setClientSecret(null);
  };

  const errorCount = Object.keys(errors)?.length;

  return (
    <div className="max-w-6xl mx-auto">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="bg-card rounded-lg shadow-md p-6 lg:p-8">
            <div className="flex items-start gap-4 mb-8">
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Icon name="MapPin" size={24} color="var(--color-primary)" />
              </div>
              <div>
                <h2 className="text-2xl font-semibold text-foreground mb-2">Finalisation et paiement</h2>
                <p className="text-muted-foreground">
                  Vos coordonnées et lieu d'intervention
                </p>
              </div>
            </div>

            {showValidationSummary && errorCount > 0 && (
              <div className="mb-6 p-4 bg-error/10 border-2 border-error/20 rounded-lg">
                <div className="flex items-start gap-3">
                  <Icon name="AlertCircle" size={24} color="var(--color-error)" className="flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-error mb-2">
                      {errorCount} {errorCount === 1 ? 'erreur détectée' : 'erreurs détectées'}
                    </h3>
                    <p className="text-sm text-error/90 mb-3">
                      Veuillez corriger les champs suivants :
                    </p>
                    <ul className="space-y-1">
                      {Object.values(errors)?.map((error, idx) => (
                        <li key={idx} className="text-sm text-error/90">• {error}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Customer Info */}
              <div>
                <h3 className="text-lg font-medium text-foreground mb-4">Vos informations</h3>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <Input
                    label="Prénom"
                    type="text"
                    placeholder="Jean"
                    value={customerInfo?.firstName}
                    onChange={(e) => handleCustomerInfoChange('firstName', e?.target?.value)}
                    error={errors?.firstName}
                    required
                  />
                  <Input
                    label="Nom"
                    type="text"
                    placeholder="Dupont"
                    value={customerInfo?.lastName}
                    onChange={(e) => handleCustomerInfoChange('lastName', e?.target?.value)}
                    error={errors?.lastName}
                    required
                  />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
                  <Input
                    label="Email"
                    type="email"
                    placeholder="jean.dupont@example.com"
                    value={customerInfo?.email}
                    onChange={(e) => handleCustomerInfoChange('email', e?.target?.value)}
                    error={errors?.email}
                    required
                  />
                  <Input
                    label="Téléphone"
                    type="tel"
                    placeholder="06 12 34 56 78"
                    value={customerInfo?.phone}
                    onChange={(e) => handleCustomerInfoChange('phone', e?.target?.value)}
                    error={errors?.phone}
                    required
                  />
                </div>
              </div>

              {/* Location Info */}
              <div className="pt-6 border-t border-border">
                <h3 className="text-lg font-medium text-foreground mb-4">Lieu d'intervention</h3>
                {isLoggedIn && (
                  <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center gap-2 text-sm text-blue-700">
                      <Icon name="Info" size={16} />
                      <span>Adresse chargée depuis votre profil</span>
                    </div>
                  </div>
                )}
                <Input
                  label="Adresse complète"
                  type="text"
                  placeholder="12 rue de la République"
                  value={customerInfo?.address}
                  onChange={(e) => handleCustomerInfoChange('address', e?.target?.value)}
                  error={errors?.address}
                  required
                />
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
                  <Input
                    label="Code postal"
                    type="text"
                    placeholder="75001"
                    maxLength={5}
                    value={customerInfo?.postalCode}
                    onChange={(e) => handleCustomerInfoChange('postalCode', e?.target?.value)}
                    error={errors?.postalCode}
                    required
                  />
                  <Input
                    label="Ville"
                    type="text"
                    placeholder="Paris"
                    value={customerInfo?.city}
                    onChange={(e) => handleCustomerInfoChange('city', e?.target?.value)}
                    error={errors?.city}
                    required
                  />
                </div>

                {/* Location Validation */}
                <div className="mt-4">
                  <Button
                    type="button"
                    variant={locationValidated ? 'default' : 'outline'}
                    size="md"
                    onClick={validateLocation}
                    loading={isValidatingLocation}
                    iconName={locationValidated ? 'CheckCircle' : 'MapPin'}
                    disabled={!customerInfo?.postalCode || !customerInfo?.city}
                  >
                    {isValidatingLocation ? 'Vérification...' : locationValidated ? 'Zone validée ✓' : 'Vérifier la zone d\'intervention'}
                  </Button>
                  {errors?.location && (
                    <div className="mt-3 p-3 bg-error/10 border border-error/20 rounded-lg">
                      <p className="text-sm text-error">{errors?.location}</p>
                    </div>
                  )}
                  {locationValidated && (
                    <div className="mt-3 p-3 bg-success/10 border border-success/20 rounded-lg">
                      <div className="flex items-center gap-2 text-sm text-success">
                        <Icon name="CheckCircle" size={16} />
                        <span>Votre zone est couverte par nos services</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Customer Type */}
              <div className="pt-6 border-t border-border">
                <label className="block text-sm font-medium text-foreground mb-3">
                  Type de client
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => handleCustomerInfoChange('customerType', 'individual')}
                    className={`p-4 rounded-lg border-2 transition-all duration-200 ${
                      customerInfo?.customerType === 'individual' ?'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                    }`}
                  >
                    <Icon
                      name="User"
                      size={24}
                      className="mx-auto mb-2"
                      color={customerInfo?.customerType === 'individual' ? 'var(--color-primary)' : 'currentColor'}
                    />
                    <p className="font-medium text-sm">Particulier</p>
                    <p className="text-xs text-muted-foreground mt-1">TVA 10%</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleCustomerInfoChange('customerType', 'business')}
                    className={`p-4 rounded-lg border-2 transition-all duration-200 ${
                      customerInfo?.customerType === 'business' ?'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                    }`}
                  >
                    <Icon
                      name="Building"
                      size={24}
                      className="mx-auto mb-2"
                      color={customerInfo?.customerType === 'business' ? 'var(--color-primary)' : 'currentColor'}
                    />
                    <p className="font-medium text-sm">Professionnel</p>
                    <p className="text-xs text-muted-foreground mt-1">TVA 20%</p>
                  </button>
                </div>
              </div>

              {/* Payment Method */}
              {!isQuoteService && !showStripeForm && (
                <div className="pt-6 border-t border-border">
                  <label className="block text-sm font-medium text-foreground mb-3">
                    Mode de paiement
                  </label>
                  <button
                    type="button"
                    onClick={() => setPaymentMethod('card')}
                    className={`w-full p-4 rounded-lg border-2 transition-all duration-200 flex items-center gap-3 ${
                      paymentMethod === 'card' ?'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                    }`}
                  >
                    <Icon name="CreditCard" size={24} />
                    <div className="flex-1 text-left">
                      <p className="font-medium text-sm">Carte bancaire</p>
                      <p className="text-xs text-muted-foreground">Paiement sécurisé par Stripe</p>
                    </div>
                    {paymentMethod === 'card' && (
                      <Icon name="CheckCircle" size={20} color="var(--color-primary)" />
                    )}
                  </button>
                </div>
              )}

              {/* Stripe Payment Form */}
              {showStripeForm && clientSecret && (
                <div className="pt-6 border-t border-border">
                  <h3 className="text-lg font-medium text-foreground mb-4">Informations de paiement</h3>
                  <StripePaymentForm
                    clientSecret={clientSecret}
                    amount={total}
                    currency="EUR"
                    orderData={{ reference: `APT-${appointmentId}` }}
                    customerInfo={customerInfo}
                    onSuccess={handlePaymentSuccess}
                    onError={handlePaymentError}
                    confirmButtonText={`Payer ${total?.toFixed(2)} €`}
                  />
                </div>
              )}

              {/* Terms & Conditions */}
              {!showStripeForm && (
                <div className="space-y-3 pt-6 border-t border-border">
                  <Checkbox
                    label="J'accepte les conditions générales de service"
                    checked={acceptTerms}
                    onChange={(e) => {
                      setAcceptTerms(e?.target?.checked);
                      if (errors?.terms) setErrors((prev) => ({ ...prev, terms: '' }));
                    }}
                    error={errors?.terms}
                  />
                  <Checkbox
                    label="J'accepte la politique de confidentialité (RGPD)"
                    description="Vos données ne sont partagées qu'avec votre accord"
                    checked={acceptRGPD}
                    onChange={(e) => {
                      setAcceptRGPD(e?.target?.checked);
                      if (errors?.rgpd) setErrors((prev) => ({ ...prev, rgpd: '' }));
                    }}
                    error={errors?.rgpd}
                  />
                  <Checkbox
                    label="Créer un compte pour suivre mes interventions"
                    description="Optionnel - Accédez à votre historique"
                    checked={createAccount}
                    onChange={(e) => setCreateAccount(e?.target?.checked)}
                  />
                </div>
              )}

              {!showStripeForm && (
                <div className="flex justify-between pt-6">
                  <Button variant="outline" size="lg" iconName="ArrowLeft" onClick={onBack} type="button">
                    Retour
                  </Button>
                  <Button
                    type="submit"
                    variant="default"
                    size="lg"
                    iconName={isQuoteService ? 'Send' : 'CreditCard'}
                    iconPosition="right"
                    loading={isProcessing}
                    disabled={!locationValidated}
                  >
                    {isProcessing
                      ? 'Traitement...'
                      : isQuoteService
                      ? 'Demander un devis' : 'Procéder au paiement'}
                  </Button>
                </div>
              )}
            </form>
          </div>
        </div>

        {/* Summary Sidebar */}
        <div className="lg:col-span-1">
          <div className="bg-card rounded-lg shadow-md p-6 sticky top-6">
            <h3 className="text-lg font-semibold text-foreground mb-4">Récapitulatif</h3>

            <div className="space-y-4">
              <div className="pb-4 border-b border-border">
                <p className="text-sm text-muted-foreground mb-1">Pièce</p>
                <p className="font-medium text-foreground">{data?.room?.name}</p>
              </div>

              <div className="pb-4 border-b border-border">
                <p className="text-sm text-muted-foreground mb-1">Équipement</p>
                <p className="font-medium text-foreground">{data?.equipment?.name}</p>
              </div>

              <div className="pb-4 border-b border-border">
                <p className="text-sm text-muted-foreground mb-1">Service</p>
                <p className="font-medium text-foreground">{data?.service?.name}</p>
              </div>

              <div className="pb-4 border-b border-border">
                <p className="text-sm text-muted-foreground mb-1">Créneau</p>
                <p className="font-medium text-foreground">{data?.urgency?.name}</p>
              </div>

              {!isQuoteService && (
                <>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Sous-total</span>
                      <span className="font-medium">{subtotal?.toFixed(2)} €</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">
                        TVA ({(vatRate * 100)?.toFixed(0)}%)
                      </span>
                      <span className="font-medium">{vat?.toFixed(2)} €</span>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-border">
                    <div className="flex justify-between items-baseline">
                      <span className="text-lg font-semibold text-foreground">Total</span>
                      <span className="text-2xl font-bold text-primary">{total?.toFixed(2)} €</span>
                    </div>
                  </div>
                </>
              )}

              {isQuoteService && (
                <div className="p-3 bg-warning/10 rounded-lg">
                  <div className="flex items-start gap-2">
                    <Icon name="FileText" size={16} className="text-warning mt-0.5" />
                    <p className="text-xs text-warning">
                      Service sur devis. Estimation après diagnostic.
                    </p>
                  </div>
                </div>
              )}

              <div className="pt-4 border-t border-border">
                <div className="flex items-start gap-2">
                  <Icon name="Shield" size={16} className="text-success mt-0.5" />
                  <p className="text-xs text-muted-foreground">
                    Garantie 6 mois • Paiement sécurisé
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentStep;